import { forwardRef } from "react";
import { action } from "./icons/action";
import { content } from "./icons/content";
import { navigation } from "./icons/navigation";
import { people } from "./icons/people";
import { system } from "./icons/system";
import { training } from "./icons/training";

/**
 * Gymkee 2027 iconography (foundation: icons; sheet DS03).
 *
 * The six generated modules under `icons/` are coach web's own, copied verbatim: one library,
 * every glyph drawn on the 24 grid with a 1.5 contour, round caps and joins, in currentColor, so
 * it inherits the text colour of the line it sits in. They are generated by
 * `identity-2027/icons/build-icons.mjs` from `icons/index.json` and are never hand-edited here;
 * the day the in-house family lands, they are re-copied.
 *
 * Two things coach web's wrapper carries that this one does not. Dwayne is absent: rule 15 and
 * gate 15.5 keep him off money and auth surfaces, and every page in this app is one or the
 * other. The legacy `icons.svg` sprite is absent too: nothing here draws from it, and shipping a
 * second library beside the first is the thing gate 7.1 exists to stop.
 */

interface IconProps extends React.SVGProps<SVGSVGElement> {
  name: IconName;
  size?: number;
}

/** Every glyph in the app, grouped by what it is for. The markup is generated, not typed. */
const glyphs = {
  ...navigation,
  ...action,
  ...content,
  ...people,
  ...training,
  ...system,
};

export type IconName = keyof typeof glyphs;

export const Icon = forwardRef<SVGSVGElement, IconProps>(
  ({ name, size = 20, width, height, ...props }, ref) => {
    const noDimensions = !width && !height && size;
    const w = noDimensions ? size : width;
    const h = noDimensions ? size : height;

    const glyph = glyphs[name];
    if (!glyph) return null;

    // The grid contract, once, for every glyph in the library:
    // viewBox 0 0 24 24, stroke-width 1.5, round caps, round joins.
    return (
      <svg
        ref={ref}
        width={w}
        height={h}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        focusable="false"
        {...props}
        dangerouslySetInnerHTML={{ __html: glyph }}
      />
    );
  },
);

Icon.displayName = "Icon";
